import { Box, Button, Container, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getApiUrl } from '../../data/api';
import { InstagramFeed as InstagramFeedType } from '../../types/home';

const InstagramFeed = () => {
  // Add Instagram feed query
  const {
    data: instagramData,
    isLoading: isLoadingInstagram,
    error: instagramError,
  } = useQuery<InstagramFeedType, Error>({
    queryKey: ['instagram'],
    queryFn: async () => {
      const res = await fetch(`${getApiUrl()}/instagram/feed`);
      if (!res.ok) throw new Error('Failed to fetch Instagram feed');
      return res.json() as Promise<InstagramFeedType>;
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });

  return (
    <Box sx={{ py: 8, bgcolor: '#fafafa' }}>
      <Container maxWidth="lg">
        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 1 }}>
          <Box 
            component="a" 
            href="https://instagram.com/begardgear" 
            target="_blank"
            aria-label="Follow Us on Instagram (opens in new tab)"
            sx={{ 
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '&:hover': {
                color: 'primary.main',
              }
            }}
          >
            <svg height="24" width="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
            </svg>
            Follow Us on Instagram
          </Box>
        </Typography>
        
        <Typography 
          variant="subtitle1" 
          align="center" 
          color="text.secondary"
          component="p"
          sx={{ mb: 4 }}
        >
          @begardgear
        </Typography>
        
        {isLoadingInstagram ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography align="center">Loading Instagram feed...</Typography>
          </Box>
        ) : instagramError ? (
          <Typography color="error" align="center">
            Error loading Instagram feed: {instagramError.message}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                position: 'relative',
                '&:hover .nav-button': {
                  opacity: 1,
                },
                mb: 3,
              }}
            >
              {/* Left Navigation Arrow */}
              <Button
                className="nav-button"
                aria-label="Scroll Instagram feed left"
                sx={{
                  position: 'absolute',
                  left: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  opacity: { xs: 1, sm: 0 },
                  transition: 'opacity 0.3s',
                  '&:hover': {
                    bgcolor: 'white',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #1976d2',
                    outlineOffset: 2,
                    opacity: 1
                  }
                }}
                onClick={() => {
                  const container = document.getElementById('instagram-carousel');
                  if (container) {
                    container.scrollBy({ left: -300, behavior: 'smooth' });
                  }
                }}
              >
                ←
              </Button>
              
              {/* Instagram Posts Carousel */}
              <Box
                id="instagram-carousel"
                sx={{
                  display: 'flex',
                  gap: 2,
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  pb: 1,
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  scrollSnapType: 'x mandatory',
                }}
              >
                {instagramData?.data && instagramData.data.length > 0 ? (
                  instagramData.data.map((post) => (
                    <Box 
                      key={post.id} 
                      sx={{ 
                        flexShrink: 0,
                        width: { xs: '85%', sm: '45%', md: '30%', lg: '23%' },
                        scrollSnapAlign: 'start',
                      }}
                    >
                      <Box 
                        sx={{ 
                          borderRadius: 2, 
                          overflow: 'hidden',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                          bgcolor: 'white',
                          transition: 'transform 0.3s',
                          WebkitBackfaceVisibility: 'hidden',
                          backfaceVisibility: 'hidden',
                          transform: 'translateZ(0)',
                          '@media (hover: hover)': {
                            '&:hover': {
                              transform: 'translateY(-4px) translateZ(0)',
                              boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                              '& .insta-overlay': {
                                opacity: 1
                              }
                            }
                          }
                        }}
                      >
                        {/* Post Header */}
                        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box 
                            component="span" 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              borderRadius: '50%', 
                              bgcolor: 'primary.light',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          >
                            B
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" component="p" sx={{ lineHeight: 1.2 }}>
                              begardgear
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(post.timestamp).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Post Image */}
                        <Box 
                          sx={{ 
                            paddingTop: '100%', // 1:1 Aspect ratio
                            position: 'relative',
                          }}
                        >
                          <Box 
                            component="img"
                            src={`${getApiUrl()}${post.thumbnail_url || post.media_url}`}
                            alt={post.caption || `Instagram post`}
                            loading="lazy"
                            onError={(e) => {
                              // Handle image loading errors gracefully
                              const target = e.target as HTMLImageElement;
                              console.warn('Failed to load Instagram image:', target.src);
                              // Replace with a fallback placeholder
                              target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDMwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjVmNWY1Ii8+CjxwYXRoIGQ9Ik0xNTAgMTAwYzEzLjgwNyAwIDI1IDExLjE5MyAyNSAyNXMtMTEuMTkzIDI1LTI1IDI1LTI1LTExLjE5My0yNS0yNSAxMS4xOTMtMjUgMjUtMjV6bTAgNzVjNDEuNDIxIDAgNzUgMzMuNTc5IDc1IDc1cy0zMy41NzkgNzUtNzUgNzUtNzUtMzMuNTc5LTc1LTc1IDMzLjU3OS03NSA3NS03NXoiIGZpbGw9IiNjY2MiLz4KPC9zdmc+';
                              target.alt = 'Instagram image unavailable';
                            }}
                            sx={{ 
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Box 
                            className="insta-overlay"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0,
                              transition: 'opacity 0.3s',
                              padding: 2,
                              overflow: 'hidden',
                            }}
                          >
                            {post.caption && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'white', 
                                  mb: 2,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  textAlign: 'center',
                                }}
                              >
                                {post.caption}
                              </Typography>
                            )}
                            <Button 
                              variant="contained" 
                              color="secondary"
                              href={post.permalink}
                              target="_blank"
                              aria-label="View Post (opens in new tab)"
                              sx={{ 
                                minWidth: 120,
                                borderRadius: 5,
                              }}
                            >
                              View Post
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography align="center" sx={{ width: '100%' }}>No Instagram posts available</Typography>
                )}
              </Box>
              
              {/* Right Navigation Arrow */}
              <Button
                className="nav-button"
                aria-label="Scroll Instagram feed right"
                sx={{
                  position: 'absolute',
                  right: -20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  opacity: { xs: 1, sm: 0 },
                  transition: 'opacity 0.3s',
                  '&:hover': {
                    bgcolor: 'white',
                  },
                  '&:focus-visible': {
                    outline: '2px solid #1976d2',
                    outlineOffset: 2,
                    opacity: 1
                  }
                }}
                onClick={() => {
                  const container = document.getElementById('instagram-carousel');
                  if (container) {
                    container.scrollBy({ left: 300, behavior: 'smooth' });
                  }
                }}
              >
                →
              </Button>
            </Box>
            
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="primary"
                endIcon={<svg height="16" width="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                  <path d="M12 6.87a5.13 5.13 0 1 0 0 10.26 5.13 5.13 0 0 0 0-10.26zm0 8.46a3.33 3.33 0 1 1 0-6.66 3.33 3.33 0 0 1 0 6.66zm6.35-8.66a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0z"/>
                </svg>}
                href="https://instagram.com/begardgear"
                target="_blank"
                aria-label="See More on Instagram (opens in new tab)"
                sx={{ fontWeight: 'bold' }}
              >
                See More on Instagram
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};

export default InstagramFeed; 